import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ModulesProvider, useModules } from "./context/ModulesContext";
import { AdminProvider } from "./context/AdminContext";
import ModulePaywall from "./components/ModulePaywall";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import RevenuePage from "./pages/RevenuePage";
import ExpensesPage from "./pages/ExpensesPage";
import PLReportPage from "./pages/PLReportPage";
import AIChatPage from "./pages/AIChatPage";
import NotesPage from "./pages/NotesPage";
import SettingsPage from "./pages/SettingsPage";
import FrontOfHousePage from "./pages/FrontOfHousePage";
import ProductMixPage from "./pages/ProductMixPage";
import StaffPage from "./pages/StaffPage";
import IngredientsPage from "./pages/IngredientsPage";
import InventoryPage from "./pages/InventoryPage";
import HealthSafetyPage from "./pages/HealthSafetyPage";
import KioskPage from "./pages/KioskPage";
import RecipesPage from "./pages/RecipesPage";
import SmartInvoicePage from "./pages/SmartInvoicePage";
import DocumentsPage from "./pages/DocumentsPage";
import ProjectsPage from "./pages/ProjectsPage";
import BoardMeetingPage from "./pages/BoardMeetingPage";
import EmailPage from "./pages/EmailPage";
import RescuePlanPage from "./pages/RescuePlanPage";
import BankStatementsPage from "./pages/BankStatementsPage";
import PayrollPage from "./pages/PayrollPage";
import InboxPage from "./pages/InboxPage";
import AdminOrgsPage from "./pages/AdminOrgsPage";
import AdminOrgDetailPage from "./pages/AdminOrgDetailPage";
import DebtsPage from "./pages/DebtsPage";
import ImportsPage from "./pages/ImportsPage";
import DealsCalendarPage from "./pages/DealsCalendarPage";
import DealDetailPage from "./pages/DealDetailPage";
import DealLibraryPage from "./pages/DealLibraryPage";
import OnboardingPage from "./pages/OnboardingPage";
import AdminInviteCodesPage from "./pages/AdminInviteCodesPage";
import ReviewsPage from "./pages/ReviewsPage";

// Layout
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isRouteEnabled, routeModule, loading: modulesLoading } = useModules();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Incomplete onboarding → send to the wizard (except if we're already there).
  if (user.org_onboarding_completed === false && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Module gating — if this route belongs to a disabled module, show paywall
  // instead of the page. Wait until modules have loaded so we don't
  // briefly render a paywall on every navigation.
  const path = location.pathname;
  const contents =
    !modulesLoading && !isRouteEnabled(path)
      ? <ModulePaywall moduleKey={routeModule(path)} routePath={path} />
      : children;

  return <Layout>{contents}</Layout>;
};

// Route gating: if the path is owned by a disabled module, show paywall instead.
const ModuleGate = ({ path, children }) => {
  const { isRouteEnabled, routeModule, loading } = useModules();
  if (loading) return children;
  if (isRouteEnabled(path)) return children;
  return <ModulePaywall moduleKey={routeModule(path)} routePath={path} />;
};

// Public Route - redirects to dashboard if logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/revenue" 
        element={
          <ProtectedRoute>
            <RevenuePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/expenses" 
        element={
          <ProtectedRoute>
            <ExpensesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pl-report" 
        element={
          <ProtectedRoute>
            <PLReportPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ai-chat" 
        element={
          <ProtectedRoute>
            <AIChatPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notes" 
        element={
          <ProtectedRoute>
            <NotesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/front-of-house" 
        element={
          <ProtectedRoute>
            <FrontOfHousePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/product-mix" 
        element={
          <ProtectedRoute>
            <ProductMixPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff" 
        element={
          <ProtectedRoute>
            <StaffPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ingredients" 
        element={
          <ProtectedRoute>
            <IngredientsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/health-safety" 
        element={
          <ProtectedRoute>
            <HealthSafetyPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/kiosk/:slug" element={<KioskPage />} />
      <Route 
        path="/recipes" 
        element={
          <ProtectedRoute>
            <RecipesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/smart-invoice" 
        element={
          <ProtectedRoute>
            <SmartInvoicePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/documents" 
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects" 
        element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/board-meeting" 
        element={
          <ProtectedRoute>
            <BoardMeetingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/email" 
        element={
          <ProtectedRoute>
            <EmailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rescue-plan" 
        element={
          <ProtectedRoute>
            <RescuePlanPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bank-statements" 
        element={
          <ProtectedRoute>
            <BankStatementsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payroll" 
        element={
          <ProtectedRoute>
            <PayrollPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inbox" 
        element={
          <ProtectedRoute>
            <InboxPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/orgs"
        element={
          <ProtectedRoute>
            <AdminOrgsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orgs/:id"
        element={
          <ProtectedRoute>
            <AdminOrgDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invite-codes"
        element={
          <ProtectedRoute>
            <AdminInviteCodesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={<Navigate to="/admin/orgs" replace />}
      />
      <Route
        path="/debts"
        element={
          <ProtectedRoute>
            <DebtsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imports"
        element={
          <ProtectedRoute>
            <ImportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/deals-calendar"
        element={
          <ProtectedRoute>
            <DealsCalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/deals-calendar/library"
        element={
          <ProtectedRoute>
            <DealLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/deals-calendar/:id"
        element={
          <ProtectedRoute>
            <DealDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/reviews"
        element={
          <ProtectedRoute>
            <ReviewsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ModulesProvider>
          <AdminProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AdminProvider>
        </ModulesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
